/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import memoizeOne, { type MemoizedFn } from 'memoize-one';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useService } from '@jira/platform__use-service/src/useService.tsx';
import type { PersonalizationResponse, SitePersonalizationRequest, Trait } from '../../types.tsx';

const getPersonalizationSiteTraitsUrl = (cloudId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}`;

export const fetchSitePersonalization = async ({
	cloudId,
}: SitePersonalizationRequest): Promise<PersonalizationResponse['attributes']> => {
	if (!cloudId) throw new Error('empty cloudId');

	const { attributes } = await fetchJson(getPersonalizationSiteTraitsUrl(cloudId), {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'x-atlassian-mau-ignore': 'true',
		},
	});

	return attributes;
};

export const memoizedFetchSitePersonalization: MemoizedFn<
	({ cloudId }: SitePersonalizationRequest) => Promise<PersonalizationResponse['attributes']>
> = memoizeOne(
	fetchSitePersonalization,
	([{ cloudId: newCloudId }], [{ cloudId: lastCloudId }]) => newCloudId === lastCloudId,
);

/**
 * Use personalisation API to fetch site traits
 */
export const useSitePersonalization = (): {
	data: Trait[] | undefined;
	loading: boolean;
	error: Error | undefined;
} & {
	fetch: (requestData: SitePersonalizationRequest) => Promise<Trait[] | undefined>;
} => useService(memoizedFetchSitePersonalization);
